import type { CSSProperties } from 'react'
import { BACCARAT_CHIP_COLORS, BACCARAT_CHIP_RAW_VALUES } from '@/config/original'
import { cn } from '@/lib/utils'

function formatChipLabel(chipIndex: number): string {
	const raw = BACCARAT_CHIP_RAW_VALUES[chipIndex] ?? 0
	if (raw >= 1_000_000) {
		return `${raw / 1_000_000}M`
	}
	if (raw >= 1_000) {
		return `${raw / 1_000}K`
	}
	return String(raw)
}

interface ChipProps {
	chipIndex: number
	size?: number
	className?: string
	style?: CSSProperties
}

/** Casino chip with inline edge-spot SVG (matches baccarat-game-main). */
export function Chip({ chipIndex, size = 25, className = '', style }: ChipProps) {
	const color = BACCARAT_CHIP_COLORS[chipIndex % BACCARAT_CHIP_COLORS.length]
	const label = formatChipLabel(chipIndex)
	const darkLabel = chipIndex === 4

	return (
		<div
			className={cn(
				'relative flex shrink-0 items-center justify-center overflow-hidden rounded-full font-bold shadow',
				darkLabel ? 'text-zinc-900' : 'text-white',
				className,
			)}
			style={{
				width: size,
				height: size,
				fontSize: Math.max(8, size * 0.32),
				...style,
			}}
		>
			<svg className='absolute inset-0 h-full w-full' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg' aria-hidden>
				<circle cx='50' cy='50' r='50' fill={color} />
				<circle cx='50' cy='50' r='42' fill='none' stroke='#ffffff' strokeWidth='14' strokeDasharray='16.5 16.5' transform='rotate(-11.25 50 50)' />
				<circle cx='50' cy='50' r='28' fill={color} />
			</svg>
			<span className='relative z-10 select-none leading-none'>{label}</span>
		</div>
	)
}
